var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import AdminPageLayout from '../../../layout/admin-page-layout';
import Wysiwyg from '@rad/components/inputs/wysiwyg';
import AwardAddEdit from './_components/award-add-edit';
import Skeleton from '@rad/components/utility/skeleton';
import ActionMenu from '@rad/components/ui/action-menu';
import { useFetch, usePromise } from '@rad/hooks';
import { getHomePageContent, updateHomePageContent, getDistrictAwards, createDistrictAward, updateDistrictAward, deleteDistrictAward } from '../../../common/network/district';
import { uploadFile } from '../../../common/network/attachment';
import { BLOB_BASE_AWARD } from '../../../common/constants';
import { createAwardState } from '../../../types';
import { LocalDate } from '@js-joda/core';
import { useToast } from '../../../contexts/toast';
import { useAuth } from '../../../hooks/useAuth';
import { Permission } from '../../../common/routes/types';
const AdminContentHomepage = () => {
    const toast = useToast();
    const [auth] = useAuth();
    const canCreate = auth.hasPermission([Permission.HomepageCreate]);
    const canUpdate = auth.hasPermission([Permission.HomepageUpdate]);
    const canDelete = auth.hasPermission([Permission.HomepageDelete]);
    const [homePageContentPromise] = useFetch(getHomePageContent());
    const homePageData = usePromise(homePageContentPromise);
    const [homePageContent, setHomePageContent] = React.useState('');
    const [savingHome, setSavingHome] = React.useState(false);
    const [homeDirty, setHomeDirty] = React.useState(false);
    const [awardsPromise, refetchAwards] = useFetch(getDistrictAwards());
    const awards = usePromise(awardsPromise);
    const [showAwardModal, setShowAwardModal] = React.useState(false);
    const [awardForm, setAwardForm] = React.useState(createAwardState());
    const [awardErrors, setAwardErrors] = React.useState({});
    const [awardFile, setAwardFile] = React.useState(null);
    React.useEffect(() => {
        if (homePageData) {
            setHomePageContent(homePageData.homePageContent);
        }
    }, [homePageData]);
    const handleHomeChange = (value) => {
        setHomePageContent(value);
        setHomeDirty(true);
    };
    const handleSaveHome = () => __awaiter(void 0, void 0, void 0, function* () {
        setSavingHome(true);
        try {
            yield updateHomePageContent(homePageContent);
            setHomeDirty(false);
            toast.success('Homepage content saved');
        }
        finally {
            setSavingHome(false);
        }
    });
    const openAddAwardModal = () => {
        setAwardForm(Object.assign(Object.assign({}, createAwardState()), { sortOrder: awards && awards.length > 0
                ? Math.max(...awards.map(a => a.sortOrder)) + 1
                : 1 }));
        setAwardErrors({});
        setAwardFile(null);
        setShowAwardModal(true);
    };
    const openEditAwardModal = (award) => {
        setAwardForm(Object.assign(Object.assign({}, award), { awardDate: award.awardDate ? LocalDate.parse(award.awardDate) : null }));
        setAwardErrors({});
        setAwardFile(null);
        setShowAwardModal(true);
    };
    const handleSaveAward = () => __awaiter(void 0, void 0, void 0, function* () {
        setAwardErrors({});
        const formToSave = Object.assign({}, awardForm);
        if (awardFile) {
            const uploaded = yield uploadFile(awardFile, 'award');
            if (uploaded) {
                formToSave.imageAttachmentId = uploaded.id;
            }
            else {
                toast.error('Failed to upload image');
                return;
            }
        }
        const result = formToSave.id
            ? yield updateDistrictAward(formToSave)
            : yield createDistrictAward(formToSave);
        if (result[0]) {
            setShowAwardModal(false);
            refetchAwards();
            toast.success(awardForm.id ? 'Award updated' : 'Award created');
        }
        else {
            const errors = {};
            result[1].forEach(err => {
                errors[err.field] = err.message;
            });
            setAwardErrors(errors);
            toast.error('Please fix the errors below');
        }
    });
    const handleDeleteAward = (award) => __awaiter(void 0, void 0, void 0, function* () {
        if (confirm(`Are you sure you want to delete ${award.title}?`)) {
            yield deleteDistrictAward(award.id);
            refetchAwards();
            toast.success('Award deleted');
        }
    });
    return (_jsx(AdminPageLayout, { children: _jsxs("div", { className: "container py-4", children: [_jsx("h5", { className: "fw-semibold mb-3", children: "Homepage" }), _jsxs("div", { className: "mb-4", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("div", { className: "fw-semibold", children: "Home Page Content" }), _jsx("button", { className: "btn btn-primary btn-sm", onClick: handleSaveHome, disabled: savingHome || !homeDirty, children: savingHome ? 'Saving...' : 'Save' })] }), _jsx(Wysiwyg, { value: homePageContent, onChange: handleHomeChange, placeholder: "Edit homepage content here...", minHeight: 280 })] }), _jsxs("div", { className: "mt-4", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("div", { className: "fw-semibold", children: "Awards" }), canCreate && (_jsx("button", { className: "btn btn-outline-secondary btn-sm", onClick: openAddAwardModal, children: "Add New" }))] }), _jsx(Skeleton, { promise: awardsPromise, shell: _jsx("div", { className: "d-flex gap-3 flex-wrap", children: [1, 2, 3, 4].map(i => (_jsx("div", { className: "border rounded bg-light", style: { width: 180, height: 180 }, children: _jsxs("div", { className: "p-2", children: [_jsx("div", { className: "w-100 rounded mb-2 bg-secondary bg-opacity-25", style: { height: 120 } }), _jsx("div", { className: "w-75 rounded bg-secondary bg-opacity-25", style: { height: 16 } })] }) }, i))) }), children: (awards) => (_jsxs("div", { className: "d-flex gap-3 flex-wrap", children: [awards.map(award => (_jsxs("div", { className: "file-preview-card border rounded p-2", style: { width: 180 }, children: [(canUpdate || canDelete) && (_jsxs(ActionMenu, { className: "file-preview-actions", render: _jsx("i", { className: "bi bi-three-dots-vertical" }), children: [canUpdate && (_jsxs(ActionMenu.Item, { onClick: () => openEditAwardModal(award), children: [_jsx("i", { className: "bi bi-pencil me-2" }), "Edit"] })), canDelete && (_jsx(ActionMenu.Item, { onClick: () => handleDeleteAward(award), children: _jsxs("span", { className: "text-danger", children: [_jsx("i", { className: "bi bi-trash me-2" }), "Delete"] }) }))] })), award.imageAttachmentId ? (_jsx("img", { src: `${BLOB_BASE_AWARD}/${award.imageAttachmentId}`, alt: award.title, className: "w-100 rounded mb-2", style: { height: 120, objectFit: 'cover' } })) : (_jsx("div", { className: "w-100 rounded mb-2 d-flex align-items-center justify-content-center bg-light", style: { height: 120 }, children: _jsx("i", { className: "bi bi-award text-muted", style: { fontSize: '2rem' } }) })), _jsx("div", { className: "fw-medium small", children: award.title }), award.recipientName && _jsx("div", { className: "text-muted small", children: award.recipientName })] }, award.id))), canCreate && (_jsx("button", { className: "border rounded d-flex align-items-center justify-content-center bg-light", style: { width: 180, height: 180 }, onClick: openAddAwardModal, children: _jsx("i", { className: "bi bi-plus-lg text-muted", style: { fontSize: '2rem' } }) }))] })) })] }), _jsx(AwardAddEdit, { isOpen: showAwardModal, value: awardForm, onChange: setAwardForm, onCancel: () => setShowAwardModal(false), onSave: handleSaveAward, errors: awardErrors, file: awardFile, onFileChange: setAwardFile, currentImageUrl: awardForm.imageAttachmentId ? `${BLOB_BASE_AWARD}/${awardForm.imageAttachmentId}` : null })] }) }));
};
export default AdminContentHomepage;
